import clsx from 'clsx';
import { useRef, useState, type ChangeEvent } from 'react';
import { BiDownload, BiPaperclip, BiTrash } from 'react-icons/bi';

import { backupLink, isMediaPending, mapMediaVariants } from '@/common/media';
import { DocumentPreviewModal } from '@/components/DocumentPreview/DocumentPreview';
import { fileTypeIcon } from '@/components/DocumentPreview/previewable';
import { TASK_DOCUMENTS_MAX, type TaskDocument } from '@/store/tasks/tasks.dto';
import { downloadFromLink } from '@/utils/download-file-from-link';
import { toast } from '@/utils/toast';

import s from './TaskFiles.module.scss';

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '';
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const FileIcon = ({ name, contentType }: { name: string; contentType: string }) => (
  <span className={s.fileIcon}>
    {fileTypeIcon(name, contentType, { size: 18, 'aria-hidden': true })}
  </span>
);

type TaskFilesProps = {
  /** Files the task already carries. */
  documents: TaskDocument[];
  /** Files picked but not uploaded yet — a task being created has no id to upload against. */
  pendingFiles?: File[];
  /** Omitted: the section is a read-only list of what is attached. */
  onAttach?: (files: File[]) => void;
  onRemoveDocument?: (id: string) => void;
  onRemovePending?: (index: number) => void;
  /** Blocks the picker while an upload or a delete is in flight. */
  isBusy?: boolean;
  /** The one row being detached goes busy, so the rest of the list stays usable. */
  removingId?: string | null;
};

/**
 * A task's files: the ones it carries, the ones waiting to go up with it, and the
 * control that adds more. Attaching and detaching are the caller's — this renders
 * the list and owns only the preview over it — so the same section serves the task
 * that already exists (uploads as you pick) and the one being created (holds the
 * files until there is an id to attach them to).
 */
export const TaskFiles = ({
  documents,
  pendingFiles = [],
  onAttach,
  onRemoveDocument,
  onRemovePending,
  isBusy,
  removingId,
}: TaskFilesProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  // Reads the task's own list, so the preview closes on its own once the file
  // under it is detached.
  const previewed = documents.find(({ id }) => id === previewId);
  const total = documents.length + pendingFiles.length;
  const isFull = total >= TASK_DOCUMENTS_MAX;

  const handlePick = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Cleared straight away, so picking the same file twice still fires onChange.
    event.target.value = '';
    if (!files.length) return;

    // Activity rejects the whole upload once the task is over its cap, so the
    // count is checked here rather than letting a 400 stand in for it.
    const remaining = TASK_DOCUMENTS_MAX - total;
    if (files.length > remaining) {
      toast.error(
        remaining > 0
          ? `Couldn’t attach the files. ${remaining} more will fit.`
          : 'Couldn’t attach the files. Please remove one first.',
      );
      return;
    }

    onAttach?.(files);
  };

  return (
    <section className={s.files}>
      <div className={s.head}>
        <p className={s.title}>Files{total > 0 && ` (${total})`}</p>
        {onAttach && (
          <>
            <input
              ref={inputRef}
              type="file"
              // Several at once — activity takes the whole selection in one PATCH.
              multiple
              className={s.input}
              onChange={handlePick}
              aria-hidden="true"
              tabIndex={-1}
            />
            <button
              type="button"
              className={s.attachBtn}
              disabled={isBusy || isFull}
              title={isFull ? `Up to ${TASK_DOCUMENTS_MAX} files per task.` : undefined}
              onClick={() => inputRef.current?.click()}
            >
              <BiPaperclip aria-hidden="true" />
              {isBusy ? 'Attaching…' : 'Attach Files'}
            </button>
          </>
        )}
      </div>

      {total === 0 ? (
        <p className={s.empty}>Please attach a file to this task.</p>
      ) : (
        <div className={s.list}>
          {documents.map((file) => (
            <div key={file.id} className={s.row}>
              {/* The row's primary action: open the file. A button, not a link —
                  the preview opens over this view rather than being somewhere to
                  navigate to. */}
              <button type="button" className={s.open} onClick={() => setPreviewId(file.id)}>
                <FileIcon name={file.name} contentType={file.content_type ?? ''} />
                <span className={s.main}>
                  <span className={s.name}>{file.name}</span>
                  <span className={s.meta}>
                    {formatFileSize(file.size)}
                    {isMediaPending(file.media_status) && ' · processing'}
                  </span>
                </span>
              </button>
              {/* Formats the preview can't render are still worth having — this is
                  the way out of it. */}
              <button
                type="button"
                className={s.action}
                aria-label={`Download ${file.name}`}
                title="Download"
                onClick={() => {
                  void downloadFromLink(file.link, file.name).catch((err: unknown) => {
                    console.error(err);
                    toast.error('Couldn’t download the file. Please try again.');
                  });
                }}
              >
                <BiDownload size={16} aria-hidden="true" />
              </button>
              {onRemoveDocument && (
                <button
                  type="button"
                  className={clsx(s.action, s.remove)}
                  disabled={isBusy || removingId === file.id}
                  aria-label={`Delete ${file.name}`}
                  title="Delete"
                  onClick={() => onRemoveDocument(file.id)}
                >
                  <BiTrash size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          ))}

          {pendingFiles.map((file, index) => (
            <div key={`${file.name}-${index}`} className={s.row}>
              <span className={s.open}>
                <FileIcon name={file.name} contentType={file.type} />
                <span className={s.main}>
                  <span className={s.name}>{file.name}</span>
                  {/* Nothing to preview or download yet — the file is still on this
                      machine, and only saving sends it up. */}
                  <span className={s.meta}>{formatFileSize(file.size)} · pending</span>
                </span>
              </span>
              {onRemovePending && (
                <button
                  type="button"
                  className={clsx(s.action, s.remove)}
                  aria-label={`Remove ${file.name}`}
                  title="Remove"
                  onClick={() => onRemovePending(index)}
                >
                  <BiTrash size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {previewed && (
        <DocumentPreviewModal
          document={{
            name: previewed.name,
            contentType: previewed.content_type ?? '',
            storageLink: previewed.link,
            mediaStatus: previewed.media_status,
            backupLink: backupLink(mapMediaVariants(previewed.link, previewed.variants)),
          }}
          onClose={() => setPreviewId(null)}
        />
      )}
    </section>
  );
};

export default TaskFiles;
